import { currenciesZodSchema } from '@mint-lib/currencies-helpers';
import { createResponseSchema, ErrorSchema } from '@mint-sdk/api-core';
import { z } from 'zod';

const DateRangeSchema = z.object({
  dateFrom: z.string(),
  dateTo: z.string(),
});

export const PlanListResponseSchema = createResponseSchema(
  z.object({
    id: z.string(),
    attributes: z.record(
      z.string(),
      z.union([z.string(), z.number(), DateRangeSchema]).nullable(),
    ),
  }),
);

export const PlanListQuerySchema = z.object({
  hash: z.string().optional(),
});

const SelectOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});
export const ColumnTypeEnum = z.enum([
  'currency',
  'date',
  'integer',
  'percent',
  'free_form_text',
  'select',
  'decimal',
  'date_range',
]);

export const ColumnListResponseSchema = createResponseSchema(
  z.object({
    id: z.string(),
    label: z.string(),
    type: ColumnTypeEnum,
    decimalPlaces: z.number().nullable(),
    isHidden: z.boolean(),
    isMandatory: z.boolean(),
    options: z.array(SelectOptionSchema).nullable(),
    disabledOptions: z.array(SelectOptionSchema).nullable(),
  }),
);

// filters
const TextFilterCondition = z.enum([
  'contains',
  'not_contains',
  'equals',
  'not_equals',
  'starts_with',
  'ends_with',
  'blank',
  'not_blank',
]);

const NumberFilterCondition = z.enum([
  'not_equals',
  'gt',
  'gte',
  'lt',
  'lte',
  'blank',
  'not_blank',
]);

const RangeFilterCondition = z.enum(['between']);
const ComparisonOperator = z.enum(['AND', 'OR']);

export const TextFilter = z.object({
  condition: TextFilterCondition,
  value: z.string().nullish(),
});

export const NumberFilter = z.object({
  condition: NumberFilterCondition,
  value: z.string().nullish(),
});

export const SetFilter = z.object({
  values: z.array(z.string()),
});

const MultiTextFilter = z.object({
  operator: ComparisonOperator,
  filters: z.array(TextFilter),
});

export const NameFilter = z.object({
  filters: z.array(SetFilter.or(MultiTextFilter).or(TextFilter)).optional(),
});

export const RequestFilterParams = z.looseObject({
  name: NameFilter.optional(),
  status: SetFilter.optional(),
});

export const FilterOptionsRequestSchema = z.object({
  filters: RequestFilterParams.nullable().optional(),
});

export const PlanListRequestSchema = z.object({
  startRow: z.number(),
  endRow: z.number(),
  ordering: z.string().nullish(),
  currency: z.string(),
  filters: RequestFilterParams.nullable().optional(),
});

const KnownResPropertiesSchema = z.object({
  name: z.array(z.string()),
  status: z.array(z.string()),
  dateFrom: z.string().nullable(),
  dateTo: z.string().nullable(),
});

const UnknownResPropertiesSchema = z.record(
  z.string(),
  z.union([z.array(z.string()), z.string(), z.null()]),
);

const AdditionalSchema = z.intersection(
  UnknownResPropertiesSchema,
  KnownResPropertiesSchema,
);

export const FilterOptionsResponseSchema = z.object({
  data: z.object({
    additional: AdditionalSchema,
  }),
  error: ErrorSchema.nullable(),
});

export const FilterOptionsQuerySchema = z.object({
  hash: z.string().optional(),
});

export const PlanTotalsRequestSchema = z.object({
  currency: z.string(),
  filters: RequestFilterParams.nullable().optional(),
});

export const PlanTotalsQuerySchema = z.object({
  hash: z.string().optional(),
});

export const PlanTotalsResponseSchema = createResponseSchema(
  z.record(
    z.string(),
    z.union([z.string(), z.number(), DateRangeSchema]).nullable(),
  ),
);

export const UserSettingsResponseSchema = createResponseSchema(
  z.object({
    id: z.string(),
    currency: currenciesZodSchema.nullable(),
  }),
);

export const UpdateUserSettingsRequestSchema = z.object({
  currency: currenciesZodSchema,
});

export type FilterParams = z.infer<typeof RequestFilterParams>;
export type ComparisonOperatorType = z.infer<typeof ComparisonOperator>;
export type TextFilterConditionType = z.infer<typeof TextFilterCondition>;
export type NumberFilterConditionType = z.infer<typeof NumberFilterCondition>;
export type FilterOptionsResponse = z.infer<typeof FilterOptionsResponseSchema>;
export type PlanTotalsRequest = z.infer<typeof PlanTotalsRequestSchema>;
export type PlanTotalsResponse = z.infer<typeof PlanTotalsResponseSchema>;
export type UserSettingsResponse = z.infer<typeof UserSettingsResponseSchema>;
export type UpdateUserSettingsRequest = z.infer<
  typeof UpdateUserSettingsRequestSchema
>;
